import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import LinkIcon from '@mui/icons-material/Link';
import {
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  FormGroup,
  RadioGroup,
  FormControlLabel,
  Radio,
  TableFooter,
  Avatar,
  IconButton,
} from '@mui/material';
import { useStyles } from '../hooks/useStyles';
import { SHeadTableCell } from '../../../../components/styledComponents/Tables';
import { API_DOMAIN } from '../../../../constants';
import { useFormatMessage } from '../../../../hooks';
import { IUser } from '../../../../interfaces/user';
import AutocompleteStaff from '../../../../components/AutocompleteStaff';
import AutocompleteStaffPhone from '../../../../components/AutocompleteStaffPhone';
import TablePaginator from '../../../../components/TablePaginator/TablePaginator';
import { TUserParamsSearch } from '../../../../components/ui/UsersFilter/types';

type TProps = {
  searchUsers: IUser[];
  searchLoading: boolean;
  searchGetUsersStaff: (data: { fio?: string; phone?: string; id?: number }) => void;
  users: IUser[];
  values: any;
  setFieldValue: any;
  pageUsers: number;
  perPageUsers: number;
  totalUsers: number;
  fetchUsers: (params: Partial<TUserParamsSearch>) => Promise<void>;
};

const BuyersList: React.FC<TProps> = ({
  searchUsers,
  searchLoading,
  searchGetUsersStaff,
  users,
  setFieldValue,
  values,
  pageUsers,
  perPageUsers,
  totalUsers,
  fetchUsers,
}) => {
  const fm = useFormatMessage();
  const [checkSearch, setCheckSearch] = useState('name');
  const [fio, setFio] = useState({} as IUser);
  const [findUser, setFindUser] = useState<IUser | null>(null);

  const intl = useIntl();
  const { classes } = useStyles();

  return (
    <>
      <FormGroup style={{ flexDirection: 'row' }}>
        <RadioGroup
          name='checkSearch'
          value={checkSearch}
          onChange={e => {
            e.currentTarget.value && setCheckSearch(e.currentTarget.value);
            setFio({} as IUser);
            setFindUser(null);
          }}
          style={{ flexDirection: 'row' }}
        >
          <FormControlLabel
            value='id'
            control={<Radio color='primary' />}
            label={intl.formatMessage({ id: 'USER.SEARCH.STAFF.ID' })}
          />
          <FormControlLabel
            value='name'
            control={<Radio color='primary' />}
            label={intl.formatMessage({ id: 'USER.SEARCH.FIO' })}
          />
          <FormControlLabel
            value='number'
            control={<Radio color='primary' />}
            label={intl.formatMessage({ id: 'USER.SEARCH.PHONE' })}
          />
        </RadioGroup>
      </FormGroup>
      {checkSearch === 'name' && (
        <AutocompleteStaff
          options={searchUsers || []}
          loading={searchLoading}
          inputValue={fio}
          editable
          label={intl.formatMessage({ id: 'USER.SEARCH.STAFF.FIO' })}
          inputClassName={{ width: '100%' }}
          searchStaff={searchGetUsersStaff}
          clearUsers={() => {
            setFindUser(null);
            searchGetUsersStaff({});
          }}
          setSelectedValue={value => {
            setFindUser(value);
          }}
          disable={false}
        />
      )}
      {checkSearch === 'number' && (
        <AutocompleteStaffPhone
          options={searchUsers || []}
          loading={searchLoading}
          label={intl.formatMessage({ id: 'USER.SEARCH.STAFF.PHONE' })}
          inputClassName={{ width: '100%' }}
          searchStaff={searchGetUsersStaff}
          clearUsers={() => {
            setFindUser(null);
            searchGetUsersStaff({});
          }}
          setSelectedValue={value => {
            setFindUser(value);
          }}
        />
      )}
      {checkSearch === 'id' && (
        <AutocompleteStaffPhone
          options={searchUsers || []}
          loading={searchLoading}
          isId
          label={intl.formatMessage({ id: 'USER.SEARCH.STAFF.ID' })}
          inputClassName={{ width: '100%' }}
          searchStaff={searchGetUsersStaff}
          clearUsers={() => {
            setFindUser(null);
            searchGetUsersStaff({});
          }}
          setSelectedValue={value => {
            setFindUser(value);
          }}
        />
      )}
      <Table aria-label='simple table'>
        <TableHead>
          <TableRow>
            <SHeadTableCell>{fm('USER.TABLE.ID')}</SHeadTableCell>
            <SHeadTableCell style={{ textAlign: 'center' }}>
              {intl.formatMessage({ id: 'SETTING.PROFILE.PHOTO' })}
            </SHeadTableCell>
            <SHeadTableCell>{fm('COMPANY.FORM.PHONE')}</SHeadTableCell>
            <SHeadTableCell>{fm('USER.TABLE.FIO')}</SHeadTableCell>
            <SHeadTableCell align='right'>{fm('USER.TABLE.ACTION')}</SHeadTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(findUser ? [findUser] : users)?.map(item => (
            <TableRow key={item.id}>
              <TableCell component='th' scope='row'>
                {item.id || '-'}
              </TableCell>
              <TableCell>
                {item.avatar ? (
                  <Avatar
                    style={{ margin: '0 auto' }}
                    src={`${API_DOMAIN}/${
                      typeof item.avatar === 'string' ? item.avatar : item.avatar.small
                    }`}
                    className={classes.green}
                  />
                ) : (
                  <div className={classes.companyNameAvatarWrapper}>
                    <span
                      className='kt-badge kt-badge--lg kt-badge--rounded kt-badge--bold kt-font-success'
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 20,
                        backgroundColor: '#566DC6FF',
                      }}
                    >
                      <b>{(item.fio || '').toUpperCase()}</b>
                    </span>
                  </div>
                )}
              </TableCell>
              <TableCell>{item.phone ? `+${item.phone}` : '-'}</TableCell>
              <TableCell>{item.fio}</TableCell>
              <TableCell align='right'>
                <div className={classes.actions}>
                  <IconButton
                    onClick={() =>
                      setFieldValue('user', item.id === values.user?.id ? null : item)
                    }
                  >
                    {item.id === values.user?.id ? (
                      <LinkOffIcon color='error' />
                    ) : (
                      <LinkIcon color='primary' />
                    )}
                  </IconButton>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePaginator
              page={pageUsers}
              realPerPage={users.length}
              perPage={perPageUsers}
              total={totalUsers}
              fetchRows={(data: any) => {
                setFindUser(null);
                fetchUsers({
                  page: data.page,
                  per_page: data.perPage,
                  roles: 'ROLE_BUYER',
                });
              }}
              label={fm('USER.TABLE.PER_PAGE')}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </>
  );
};

export default BuyersList;
